import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { CustomerModel } from 'pages/Customers/helpers/types'
import { isCryptoCustomerWallet } from 'pages/Customers/helpers/utils'
import { MovePayload } from 'pages/NestedCustomers/types'
import { SourceWallet } from 'types/move/move'
import {
  checkIsCustomerWalletTransferType,
  checkIsEmbeddedOrRelianceModel,
  checkIsPrincipalWallet,
  checkIsTransferTypeSelected,
  checkIsWalletTransferTypeEnabled,
  checkRequiresCustomerReference,
  checkShowContent,
  checkShowDestinationWalletCombobox,
  checkShowNoWalletsCallout,
} from '../utils/utils'

interface UseMoveFormConditionsProps {
  sourceWallet: SourceWallet | null
  customerModel?: CustomerModel
  hasTransferWallets: boolean
  isLoading: boolean
  formValues: MovePayload
  isInsufficientBalance: boolean
  isExceedingMaxAmount: boolean
}

export const useMoveFormConditions = ({
  sourceWallet,
  customerModel,
  hasTransferWallets,
  isLoading,
  formValues,
  isInsufficientBalance,
  isExceedingMaxAmount,
}: UseMoveFormConditionsProps) => {
  const { accountCapabilities } = useAccountsContext()
  const { isEpcWalletInternalTransfersCapabilityEnabled } = accountCapabilities || {}

  const isCryptoWallet = isCryptoCustomerWallet(sourceWallet?.currency)
  const isPrincipalWallet = checkIsPrincipalWallet(sourceWallet?.walletType)
  const isEmbeddedOrRelianceCustomerModel = checkIsEmbeddedOrRelianceModel(customerModel)
  const isCustomerWalletTransferType = checkIsCustomerWalletTransferType(
    formValues?.walletTransferType
  )

  const isWalletTransferTypeEnabled = checkIsWalletTransferTypeEnabled({
    isEmbeddedOrRelianceModel: isEmbeddedOrRelianceCustomerModel,
    isPrincipalWallet,
    isCryptoWallet,
    isEpcCapabilityEnabled: isEpcWalletInternalTransfersCapabilityEnabled,
  })

  const customerHasNoModel = !customerModel

  const isTransferTypeSelected = checkIsTransferTypeSelected(formValues?.walletTransferType)
  const requiresCustomerReference = checkRequiresCustomerReference({
    isCustomerWalletTransferType,
    customerReference: formValues?.customerReference,
  })

  const showDestinationWalletCombobox = checkShowDestinationWalletCombobox({
    isTransferTypeSelected,
    hasTransferWallets,
    requiresCustomerReference,
  })

  const showNoWalletsCallout = checkShowNoWalletsCallout({
    isLoading,
    hasTransferWallets,
    walletTransferType: formValues?.walletTransferType,
    sourceWalletId: sourceWallet?.walletId,
    customerReference: formValues?.customerReference,
    isCustomerWalletTransferType,
  })

  const showContent = checkShowContent({
    isLoading,
    hasTransferWallets,
    destinationWallet: formValues?.destinationWallet,
    isTransferTypeSelected,
    requiresCustomerReference,
  })

  const isDisabled =
    isLoading || isInsufficientBalance || isExceedingMaxAmount || showNoWalletsCallout

  return {
    isEmbeddedOrRelianceCustomerModel,
    isCustomerWalletTransferType,
    isWalletTransferTypeEnabled,
    customerHasNoModel,
    isTransferTypeSelected,
    requiresCustomerReference,
    showDestinationWalletCombobox,
    showNoWalletsCallout,
    showContent,
    isDisabled,
  }
}
